import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from '../dialogs/dialog';
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useThemesData } from "../../providers/theme-management-provider";
import { FONT_LIBRARY, FontResource, getFontFamilyName } from "../../general/utilities";

export interface ThemeImport extends FontResource {
    id: string;
}

export const ManageImportsDialog: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => {
    const { cssSelectors, bulkUpdateCssProperties, removeSelector } = useThemesData();
    const [imports, setImports] = useState<ThemeImport[]>([]);
    const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(0);

    useEffect(() => {
        if (visible) {
            const existing = cssSelectors
                .flatMap(c => c.children)
                .filter(n => n.selector.startsWith("__import_"))
                .map(n => {
                    const url = n.properties.find(p => p.property === "url")?.value || "";
                    return {
                        id: n.selector,
                        url: url,
                        name: getFontFamilyName(url)
                    };
                });
            setImports(existing);
        }
    }, [visible, cssSelectors]);

    const addImport = () => {
        const newId = `__import_${crypto.randomUUID().slice(0, 8)}`;
        setImports([...imports, { id: newId, url: "", name: "" }]);
        setActiveIndex(imports.length);
    };

    const updateImport = (id: string, url: string) => {
        setImports(imports.map(i => i.id === id ? { ...i, url } : i));
    };

    const removeImport = (id: string) => {
        setPendingDeletions([...pendingDeletions, id]);
        setImports(imports.filter(i => i.id !== id));
        setActiveIndex(0);
    };

    const handleApply = () => {
        pendingDeletions.forEach(id => removeSelector(id));
        imports.forEach(imp => {
            bulkUpdateCssProperties(imp.id, [{ property: "url", value: imp.url }], 'replace');
        });
        onHide();
    };

    const availableFonts = useMemo(() => 
        FONT_LIBRARY.filter(f => !imports.some(i => i.url === f.url)), 
    [imports]);

    return (
        <Dialog 
            header="Manage CSS imports" 
            visible={visible} 
            onHide={onHide} 
            style={{ width: '80vw', height: '80vh' }}
            contentStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0 }}
        >
            <div className="flex-grow-1 overflow-y-auto">
                <div className="grid h-full m-0">
                    <div className="col-6 border-right-1 surface-border flex flex-column">
                        <div className="flex-grow-1 overflow-y-auto">
                            {imports.map((imp, idx) => (
                                <div key={imp.id} className={`p-3 border-bottom-1 surface-border cursor-pointer}`} onClick={() => setActiveIndex(idx)} style={{ 
                                        backgroundColor: activeIndex === idx ? 'var(--primary-color)' : 'transparent',
                                        color: activeIndex === idx ? 'var(--primary-color-text)' : 'inherit'
                                    }}>
                                    <div className="font-bold cursor-pointer">{getFontFamilyName(imp.url)}</div>
                                    <div className="text-sm text-overflow-ellipsis overflow-hidden white-space-nowrap cursor-pointer" title={imp.url}>{imp.url}</div>
                                </div>
                            ))}
                        </div>
                        <Button label="Add new import" icon="pi pi-plus" className="p-button-outlined m-3" onClick={addImport} />
                    </div>

                    <div className="col-6 p-4">
                        {imports[activeIndex] ? (
                            <>
                                <div className="field mb-4">
                                    <label className="block mb-2 font-bold">Source URL</label>
                                    <InputText className="w-full" value={imports[activeIndex].url} 
                                        onChange={(e) => updateImport(imports[activeIndex].id, e.target.value)}
                                        placeholder="Specify the url or select a font from the drop down below..." />
                                </div>
                                <div className="field mb-4">
                                    <label className="block mb-2 font-bold">Preset library</label>
                                    <Dropdown className="w-full" options={availableFonts} optionLabel="name" optionValue="url"
                                        value={imports[activeIndex].url} onChange={(e) => updateImport(imports[activeIndex].id, e.value)} 
                                        placeholder="Select a font..." scrollHeight={'500px'}/>
                                </div>
                                <div className="field mt-auto">
                                    <label className="block mb-2 font-bold">Font preview</label>
                                    <iframe 
                                        className="w-full h-15rem border-1 surface-border border-round"
                                        title="font-preview"
                                        srcDoc={`<html><head><link href="${imports[activeIndex].url}" rel="stylesheet"></head>
                                            <body style="font-family: '${getFontFamilyName(imports[activeIndex].url)}', sans-serif; padding: 1rem;">
                                                <p style="font-size: 1.5rem;">The quick brown fox jumps over the lazy dog.</p>
                                            </body></html>`}
                                    />
                                </div>
                                <Button label="Remove import" icon="pi pi-trash" className="p-button-danger p-button-text mt-3" 
                                    onClick={() => removeImport(imports[activeIndex].id)} />
                            </>
                        ) : (
                            <div className="h-full flex align-items-center justify-content-center text-secondary">Select an import to configure.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="admin-toolbar flex justify-content-end gap-2 p-3 border-top-1 surface-border">
                <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-text" />
                <Button label="Apply" icon="pi pi-check" onClick={handleApply} className="p-button-primary" />
            </div>
        </Dialog>
    );
};
